import { t } from 'i18next';
import Menu, { Divider, MenuItem, SubMenu } from 'rc-menu';
import { FC } from 'react';
import { menuBuiltinPlacements } from '../../../../../../shared/frontend/components/menu/menuHelper';
import { MainNavLoginButton, MainNavSignupButton } from '../../../../../../shared/frontend/components/mainNav/buttons';
import { handleRCMenuOnClick } from '../../../../../../shared/frontend/libs/history';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { LevelCap } from '../../../../shared/enums';
import type { TClaimedCharacter } from '../../../../shared/types/characterTypes';
import { withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { ClassIcon } from '../../../../../../shared/frontend/scripts/wow/components/ClassIcon';
import { DropdownTitle } from '../presentational/dropdownTitle';
import { MessageCenterIcon } from '../presentational/messageCenterIcon';
import { UserAvatarIcon } from '../../../../../../shared/frontend/components/presentational/userAvatarIcon';

type TCharacterLinkInfoProps = {
  character: TClaimedCharacter;
};

const CharacterLinkInfo: FC<TCharacterLinkInfoProps> = (props: TCharacterLinkInfoProps) => {
  const { character } = props;
  return (
    <div className="slds-grid slds-grid--align-spread slds-grid--vertical-align-center">
      <div className="slds-col slds-grow slds-text-align--left">
        <span className="slds-avatar slds-avatar--x-small slds-m-right--xx-small rio-border--light rio-shadow--small">
          <img src={asset(`/images/site/races/${character.details.race.slug}_${character.details.gender}.jpg`)} />
        </span>
        <span className="slds-avatar slds-avatar--x-small slds-m-right--x-small rio-border--light rio-shadow--small">
          <ClassIcon size={18} class={character.details.class.slug} />
        </span>
        <span className={`class-color--${character.details.class.id} rio-horiz-bar--container`}>{character.name}</span>
      </div>
      <div className="slds-col slds-text-align--right text-muted slds-text-body--small">
        {character.realm.name} ({character.region.short_name})
      </div>
    </div>
  );
};

type TLargeMainCharacterOptionProps = {
  mainCharacter: any;
  altCharacters: any[];
  favoriteCharacters: any[];
  otherCharacters: any[];
  siteMeta: any;
};

const makeLargeMainCharacterOption = (props: TLargeMainCharacterOptionProps) => {
  const { mainCharacter, altCharacters, favoriteCharacters, otherCharacters } = props;

  if (!mainCharacter || !mainCharacter.details) {
    return null;
  }

  return (
    <SubMenu
      className="rio-auth-nav__button"
      key={mainCharacter.details.path}
      onTitleClick={handleRCMenuOnClick}
      title={
        <span>
          <span className="slds-avatar slds-avatar--x-small slds-m-right--xx-small rio-border--light rio-shadow--small">
            <img
              src={asset(`/images/site/races/${mainCharacter.details.race.slug}_${mainCharacter.details.gender}.jpg`)}
            />
          </span>
          <span className="slds-avatar slds-avatar--x-small slds-m-right--x-small rio-border--light rio-shadow--small">
            <ClassIcon size={18} class={mainCharacter.details.class.slug} />
          </span>
          <span className={`class-color--${mainCharacter.details.class.id}`}>{mainCharacter.name}</span>
        </span>
      }
    >
      {favoriteCharacters.length > 1 && (
        <SubMenu
          title={
            <span>
              <i className="fa fa-fw fa-star" /> &nbsp; {t('nav:auth.dropdowns.favorites')}
            </span>
          }
        >
          {favoriteCharacters.map((ch: any) => (
            <MenuItem key={ch.details.path}>
              <CharacterLinkInfo character={ch} />
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {altCharacters.length > 0 && (
        <SubMenu
          title={
            <span>
              <i className="fa fa-fw fa-user-alt" /> &nbsp; {t('nav:auth.dropdowns.max-level-alts')}
            </span>
          }
        >
          {altCharacters.map((ch: any) => (
            <MenuItem key={ch.details.path}>
              <CharacterLinkInfo character={ch} />
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {otherCharacters.length > 0 && (
        <SubMenu
          title={
            <span>
              <i className="fa fa-fw fa-user-group" /> &nbsp; {t('nav:auth.dropdowns.other-characters')}
            </span>
          }
        >
          {otherCharacters.map((ch: any) => (
            <MenuItem key={ch.details.path}>
              <CharacterLinkInfo character={ch} />
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {/* <Divider /> */}

      {mainCharacter && mainCharacter.details && mainCharacter.details.guild && (
        <MenuItem
          key={resolveRoutePath(namedRoutes.GuildDetails, {
            region: mainCharacter.details.guild.region.slug,
            realm: mainCharacter.details.guild.realm.slug,
            guild: mainCharacter.details.guild.name
          })}
        >
          <i className="fa fa-fw fa-users-class" /> &nbsp;{' '}
          <span className={`rio-faction-color--${mainCharacter.details.guild.faction}`}>
            &lt;
            {mainCharacter.details.guild.name}
            &gt;
          </span>
        </MenuItem>
      )}

      {/* {mainCharacter && mainCharacter.details && mainCharacter.details.guild && <Divider />} */}

      <MenuItem
        key={resolveRoutePath(namedRoutes.RealmHome, {
          region: mainCharacter.region.slug,
          realm: mainCharacter.realm.isConnected ? `connected-${mainCharacter.realm.slug}` : mainCharacter.realm.slug
        })}
      >
        <i className="fa fa-fw fa-ballot" /> &nbsp; {mainCharacter.realm.name} ({mainCharacter.region.short_name})
      </MenuItem>
    </SubMenu>
  );
};

export const LargeAuthBarUnauthenticatedColumns: FC<any> = (props) => {
  return (
    <>
      <div className="slds-col slds-p-right--x-small">
        <MainNavLoginButton />
      </div>
      <div className="slds-col slds-p-right--x-small">
        <MainNavSignupButton />
      </div>
    </>
  );
};

export const LargeAuthBarAuthenticatedColumns = withSiteMeta((props: any) => {
  let mainCharacter: any = null;
  if (props.auth.claimedCharacters.length > 0) {
    mainCharacter = props.auth.claimedCharacters.find((ch: any) => ch.is_main_character);
  }

  const activeCharacters = props.auth.claimedCharacters.filter((ch: any) => ch.details);

  const altCharacters = activeCharacters.filter(
    (ch: any) => ch !== mainCharacter && ch.details.level >= LevelCap.Current
  );

  const otherCharacters = activeCharacters.filter(
    (ch: any) => ch !== mainCharacter && ch.details.level < LevelCap.Previous
  );

  const favoriteCharacters = activeCharacters.filter((ch: any) => ch.favorite);

  return (
    <>
      <div className="slds-col slds-text-align--right">
        <Menu
          onClick={handleRCMenuOnClick}
          disabledOverflow={true}
          selectable={false}
          mode="horizontal"
          builtinPlacements={menuBuiltinPlacements}
          className="no-background"
        >
          {activeCharacters.length > 0 && mainCharacter && !props.auth.avatar && (
            <MenuItem className="text-raiderio" key={'/settings/profile'}>
              {t('nav:auth.desktop.choose-avatar-prompt')}
            </MenuItem>
          )}

          {makeLargeMainCharacterOption({
            mainCharacter: mainCharacter,
            altCharacters: altCharacters,
            favoriteCharacters: favoriteCharacters,
            otherCharacters: otherCharacters,
            siteMeta: props.siteMeta
          })}

          {props.auth.claimedCharacters.length === 0 && (
            <MenuItem className="text-raiderio" key="/settings/battlenet">
              {t('nav:auth.desktop.claim-characters-prompt')}
            </MenuItem>
          )}
          {activeCharacters.length > 0 && !mainCharacter && (
            <MenuItem className="text-raiderio" key={`/settings/characters/${activeCharacters[0].details.id}/profile`}>
              {t('nav:auth.desktop.choose-main-character-prompt')}
            </MenuItem>
          )}
          {props.currentFeatures.message_center && (
            <MenuItem key={resolveRoutePath(namedRoutes.MessageCenterHomePage)}>
              <MessageCenterIcon size="xx-small" />
            </MenuItem>
          )}
        </Menu>
      </div>
      <div className="slds-col slds-grow-none slds-p-left--xx-small">
        <Menu
          onClick={handleRCMenuOnClick}
          disabledOverflow={true}
          selectable={false}
          mode="horizontal"
          builtinPlacements={menuBuiltinPlacements}
          className="no-background"
        >
          <SubMenu
            key={resolveRoutePath(namedRoutes.UserProfile, { name: props.auth.name })}
            onTitleClick={handleRCMenuOnClick}
            className="rio-menu--small-buttons"
            title={
              props.auth.avatar ? (
                <UserAvatarIcon icon={props.auth.avatar} size="x-small" />
              ) : (
                <i className="fas fa-user-circle" />
              )
            }
          >
            <MenuItem key={resolveRoutePath(namedRoutes.UserProfile, { name: props.auth.name })}>
              <i className="fa fa-fw fa-user" /> &nbsp; {props.auth.name}
            </MenuItem>
            <Divider />
            <MenuItem key="/settings">
              <i className="fa fa-fw fa-cog" /> &nbsp; {t('nav:auth.dropdowns.settings')}
            </MenuItem>
            {props.currentFeatures.recruitment_view && (
              <MenuItem key="/settings/recruitment/profiles">
                <i className="fa fa-fw fa-people-arrows-left-right" /> &nbsp;{' '}
                {t('nav:auth.dropdowns.recruitment-profiles')}
              </MenuItem>
            )}
            {(props.currentFeatures.admin ||
              props.currentFeatures.news_editor ||
              props.currentFeatures.news_admin ||
              props.currentFeatures.threads_editor ||
              props.currentFeatures.threads_admin ||
              props.siteConfig.isImpersonating) && (
              <>
                <Divider />
                <MenuItem key="/admin">
                  <i className="fa fa-fw fa-toolbox" /> &nbsp; Dashboard
                </MenuItem>
              </>
            )}
            {props.siteConfig.isImpersonating && (
              <MenuItem key="/stop-impersonate">
                <i className="fa fa-fw fa-user-slash" /> &nbsp;{' '}
                <b className="text-raiderio">Stop Impersonating &nbsp;</b>
              </MenuItem>
            )}
            <Divider />
            <MenuItem key="/logout">
              <i className="fa fa-fw fa-sign-out-alt" /> &nbsp; {t('nav:auth.logout')}
            </MenuItem>
          </SubMenu>
        </Menu>
      </div>
    </>
  );
});

export const SmallAuthBarUnauthenticatedColumns = () => {
  return (
    <div className="slds-grid slds-grid--align-end rio-auth-nav">
      <MainNavLoginButton className="slds-col slds-grow-none slds-m-right--xx-small" />
      <MainNavSignupButton className="slds-col slds-grow-none slds-m-right--xx-small" />
    </div>
  );
};

export const SmallAuthBarAuthenticatedColumns = withSiteMeta((props: any) => {
  let mainCharacter: any = null;
  if (props.auth.claimedCharacters.length > 0) {
    mainCharacter = props.auth.claimedCharacters.find((ch: any) => ch.is_main_character);
  }

  const activeCharacters = props.auth.claimedCharacters.filter((ch: any) => ch.details);

  return (
    <div className="rio-auth-nav slds-text-align--right">
      <Menu
        onClick={handleRCMenuOnClick}
        disabledOverflow={true}
        selectable={false}
        mode="horizontal"
        builtinPlacements={menuBuiltinPlacements}
        className="no-background"
      >
        {activeCharacters.length > 0 && mainCharacter && !props.auth.avatar && (
          <MenuItem className="text-raiderio" key={'/settings/profile'}>
            {t('nav:auth.mobile.choose-avatar-prompt')}
          </MenuItem>
        )}

        {mainCharacter && mainCharacter.details && (
          <SubMenu
            title={
              <DropdownTitle
                title={<span className={`class-color--${mainCharacter.details.class.id}`}>{mainCharacter.name}</span>}
              />
            }
          >
            <MenuItem key={resolveRoutePath(namedRoutes.UserProfile, { name: props.auth.name })}>
              <span>
                <UserAvatarIcon icon={props.auth.avatar} size="x-small" /> &nbsp; {props.auth.name}
              </span>
            </MenuItem>

            {props.currentFeatures.message_center && (
              <MenuItem key={resolveRoutePath(namedRoutes.MessageCenterHomePage)}>
                <span>
                  <MessageCenterIcon icon={props.auth.avatar} size="x-small" /> &nbsp;{' '}
                  {t('nav:auth.dropdowns.messages')}
                </span>
              </MenuItem>
            )}
            <Divider />

            <MenuItem key={mainCharacter.details.path}>
              <span className={`class-color--${mainCharacter.details.class.id}`}>
                {t('nav:auth.dropdowns.view-character')}
              </span>
            </MenuItem>

            {mainCharacter.details.guild && (
              <MenuItem
                className={`rio-faction-color--${mainCharacter.details.guild.faction}`}
                key={resolveRoutePath(namedRoutes.GuildDetails, {
                  region: mainCharacter.details.guild.region.slug,
                  realm: mainCharacter.details.guild.realm.slug,
                  guild: mainCharacter.details.guild.name
                })}
              >
                &lt;
                {mainCharacter.details.guild.name}
                &gt;
              </MenuItem>
            )}

            <MenuItem
              key={resolveRoutePath(namedRoutes.RaidRealmRankings, {
                raid: props.siteConfig.currentRaidSlug,
                difficulty: props.siteConfig.currentRaidDifficulty, // TODO: pick right difficulty
                region: mainCharacter.region.slug,
                realm: mainCharacter.realm.isConnected
                  ? `connected-${mainCharacter.realm.slug}`
                  : mainCharacter.realm.slug
              })}
            >
              {t('nav:auth.dropdowns.realm-raid-rankings')}
            </MenuItem>
            <Divider />
            <MenuItem key="/settings">{t('nav:auth.dropdowns.settings')}</MenuItem>
            {props.auth.claimedCharacters && props.auth.claimedCharacters.length > 0 && (
              <SubMenu title={<span>Characters</span>}>
                {activeCharacters.map((ch: any, index: number) => (
                  <MenuItem key={ch.details.path} className={`class-color--${ch.details.class.id}`}>
                    {ch.name}
                  </MenuItem>
                ))}
              </SubMenu>
            )}
            <Divider />
            <MenuItem key="/logout">{t('nav:auth.logout')}</MenuItem>
          </SubMenu>
        )}

        {props.auth.claimedCharacters.length === 0 && (
          <MenuItem className="text-raiderio" key="/settings/battlenet">
            {t('nav:auth.mobile.claim-characters-prompt')}
          </MenuItem>
        )}

        {activeCharacters.length > 0 && !mainCharacter && (
          <MenuItem className="text-raiderio" key={`/settings/characters/${activeCharacters[0].details.id}/profile`}>
            {t('nav:auth.mobile.choose-main-character-prompt')}
          </MenuItem>
        )}
      </Menu>
    </div>
  );
});
